/**
 * URL Security Utilities
 * Provides functions for validating and sanitizing URLs to prevent phishing and XSS attacks
 */

/**
 * Validates and sanitizes URLs to prevent phishing attacks
 * @param url - The URL to validate
 * @returns Sanitized URL or null if invalid
 */
export function validateAndSanitizeUrl(url: string): string | null {
  if (!url || typeof url !== 'string') {
    console.error('Invalid URL provided:', url);
    return null;
  }

  try {
    // Remove any potential XSS characters
    const sanitizedUrl = url
      .trim()
      .replace(/[<>"']/g, '')
      .replace(/javascript:/gi, '')
      .replace(/data:/gi, '')
      .replace(/vbscript:/gi, '');

    // Create URL object to validate structure
    const urlObj = new URL(sanitizedUrl, window.location.origin);

    // Define allowed domains/origins for this application
    const allowedDomains = [
      window.location.hostname, // Current domain
      'cemex.com',
      'cemexgo.com',
      'ideas.aha.io', // Feedback URLs
      // Add other trusted domains as needed
    ];

    // Define allowed protocols
    const allowedProtocols = ['http:', 'https:'];

    // Check if protocol is allowed
    if (!allowedProtocols.includes(urlObj.protocol)) {
      console.error('Blocked unsafe protocol:', urlObj.protocol);
      return null;
    }

    // For absolute URLs, check if domain is allowed
    if (
      urlObj.hostname &&
      !allowedDomains.some((domain) => urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain))
    ) {
      console.error('Blocked potentially unsafe domain:', urlObj.hostname);
      return null;
    }

    // For relative URLs starting with '/', allow them
    if (sanitizedUrl.startsWith('/')) {
      return sanitizedUrl;
    }

    // Return the validated URL
    return urlObj.toString();
  } catch (error) {
    console.error('URL validation failed:', error);
    return null;
  }
}

/**
 * Safe navigation method that validates URLs before redirecting
 * @param url - The URL to navigate to
 * @param target - Optional target for navigation ('_blank', '_self', etc.)
 */
export function safeNavigate(url: string, target?: string): void {
  const validatedUrl = validateAndSanitizeUrl(url);

  if (validatedUrl) {
    if (target === '_blank') {
      window.open(validatedUrl, '_blank');
    } else {
      window.location.href = validatedUrl;
    }
  } else {
    console.error('Navigation blocked - potentially unsafe URL:', url);
    // Optionally show user notification about blocked navigation
    // Could dispatch an event or call a notification service here
  }
}

/**
 * Safe window.open that validates URLs before opening
 * @param url - The URL to open
 * @param target - Target window name
 * @param features - Window features string
 */
export function safeWindowOpen(url: string, target?: string, features?: string): Window | null {
  const validatedUrl = validateAndSanitizeUrl(url);

  if (validatedUrl) {
    return window.open(validatedUrl, target, features);
  } else {
    console.error('Window open blocked - potentially unsafe URL:', url);
    return null;
  }
}

/**
 * Validate if a URL is safe for use in href attributes
 * @param url - The URL to validate
 * @returns boolean indicating if URL is safe
 */
export function isUrlSafe(url: string): boolean {
  return validateAndSanitizeUrl(url) !== null;
}
